/**
 * Accounts on servers, as this GUI sees them: who it is signed in as on each server, their
 * teams and spaces, and managing a team (`client/accounts.ts` makes the calls).
 *
 * Like Miro, one person can be signed in to several places at once: the page's own server, a
 * team's hosted one, a studio's own. The desktop app has no server of its own: it adds servers
 * by address, and keeps its own board offline meanwhile. Sessions are kept by this browser
 * (one per server); a session the server no longer knows (signed out elsewhere) is dropped.
 *
 * An invite link (`<server>/join/<code>`) opened in a page, or pasted, becomes the
 * {@link Accounts.pending} invite until it is accepted while signed in to its server.
 *
 * An account with the second step on answers a right password with "a code is needed": the
 * service then holds {@link Accounts.codeNeeded} (which server, and the Google or GitHub
 * ticket if it came from one), and the sign-in form asks the code and signs in again with it.
 */
import { createSignal, type Accessor } from "solid-js";
import type { InviteInfo } from "@protocol/InviteInfo";
import type { Me } from "@protocol/Me";
import type { Role } from "@protocol/Role";
import type { SpaceSummary } from "@protocol/SpaceSummary";
import type { TeamInvite } from "@protocol/TeamInvite";
import type { TeamSummary } from "@protocol/TeamSummary";
import type { TeamView } from "@protocol/TeamView";
import { accountsApi, isApiError, loadSessions, saveSessions, type AccountsApi, type InviteLimits, type SignedIn } from "../client/accounts";
import type { InviteLink } from "../client/space";
import { toast } from "../shared/toast";

/** An invite waiting to be accepted, and what it leads to (once known). */
export interface PendingInvite {
  /** Its link. */
  link: InviteLink;
  /** Its team and role (`null` while asking, or when the server refused it). */
  info: InviteInfo | null;
  /** Whether the server refused it (used, withdrawn, or never was). */
  gone?: boolean;
}

/** A sign-in waiting for the code of its second step. */
export interface CodeRequest {
  /** The server signing in. */
  server: string;
  /** The ticket a sign-in with Google or GitHub came back with (sent again with the code), if it was one. */
  ticket?: string;
}

/** What the {@link Accounts} service offers. */
export interface Accounts {
  /** The page's own server (a page it served), where signing in starts; `null` in the desktop app. */
  homeServer: string | null;
  /** Every session, oldest first. */
  sessions: Accessor<SignedIn[]>;
  /** The account and teams on `server` (`null` until loaded). */
  me(server: string): Me | null;
  /** A team as last loaded (`null` until {@link Accounts.loadTeam}). */
  team(server: string, id: string): TeamView | null;
  /** Asks `server` again for the account and its teams. */
  refresh(server: string): Promise<void>;
  /** Loads (or reloads) a team. */
  loadTeam(server: string, id: string): Promise<void>;
  /** Signs in to `server` (an address, `http://` added when missing; `code`: the second step's). Says whether it worked. */
  signIn(server: string, email: string, password: string, code?: string): Promise<boolean>;
  /** Makes an account on `server` and signs in. */
  signUp(server: string, email: string, name: string, password: string): Promise<boolean>;
  /** Signs in to `server` with the ticket a sign-in with Google or GitHub came back with (`code`: the second step's). */
  finishSignIn(server: string, ticket: string, code?: string): Promise<boolean>;
  /** The sign-in waiting for a code from the authenticator app, if one is. */
  codeNeeded: Accessor<CodeRequest | null>;
  /** Gives up the sign-in waiting for a code. */
  cancelCode(): void;
  /** Deletes your account on `server` (your password, or your email when it has none; `code` with a second step); the session goes with it. */
  deleteAccount(server: string, password: string, code?: string): Promise<boolean>;
  /** Signs out of `server` (forgets the session here even if the server cannot be reached). */
  signOut(server: string): Promise<void>;
  /** Makes a team. */
  createTeam(server: string, name: string): Promise<TeamSummary | null>;
  /** Makes a space in a team. */
  createSpace(server: string, team: string, name: string): Promise<SpaceSummary | null>;
  /** Owners: a new invite (for `limits.days`, by default the server's two weeks; for `limits.maxUses` people at most). */
  invite(server: string, team: string, role: Role, once: boolean, limits?: InviteLimits): Promise<TeamInvite | null>;
  /** Owners: withdraws an invite. */
  revoke(server: string, team: string, code: string): Promise<void>;
  /** Owners: changes a member's role. */
  setRole(server: string, team: string, member: string, role: Role): Promise<void>;
  /** Owners remove someone; anyone leaves with their own id. */
  removeMember(server: string, team: string, member: string): Promise<void>;
  /** The invite waiting to be accepted. */
  pending: Accessor<PendingInvite | null>;
  /** Makes `link` the pending invite and asks what it leads to. */
  offer(link: InviteLink): Promise<void>;
  /** Accepts the pending invite (signed in to its server). */
  accept(): Promise<TeamSummary | null>;
  /** Forgets the pending invite. */
  dismiss(): void;
  /**
   * Runs any other call on `server` as its session (presets...): a refusal becomes an error
   * toast and `null` (signed out there: the session is forgotten). With `quiet`, no toast.
   */
  call<T>(server: string, call: (api: AccountsApi) => Promise<T>, quiet?: boolean): Promise<T | null>;
}

/** What {@link createAccounts} needs. */
export interface AccountsDeps {
  /** The page's own server, if a server served it. */
  homeServer: string | null;
  /** An invite the page was opened with (`/join/<code>`). */
  invite?: InviteLink | null;
  /** The API of a server as a session (tests give a fake). */
  api?: (server: string, token?: string) => AccountsApi;
}

/** `text` as a server address: `http://` added when missing, no path, no trailing slash. */
export function serverAddress(text: string): string | null {
  const trimmed = text.trim();
  if (!trimmed) return null;
  try {
    const url = new URL(/^https?:\/\//i.test(trimmed) ? trimmed : `http://${trimmed}`);
    return url.origin;
  } catch {
    return null;
  }
}

/** Starts the service. Call inside a Solid root. */
export function createAccounts(deps: AccountsDeps): Accounts {
  const api = deps.api ?? ((server: string, token?: string) => accountsApi(server, token));
  const [sessions, setSessions] = createSignal<SignedIn[]>(loadSessions());
  const [mes, setMes] = createSignal<Record<string, Me>>({});
  const [teams, setTeams] = createSignal<Record<string, TeamView>>({});
  const [pending, setPending] = createSignal<PendingInvite | null>(null);
  const [codeNeeded, setCodeNeeded] = createSignal<CodeRequest | null>(null);

  /** Changes the sessions and keeps them. */
  const keep = (list: SignedIn[]) => {
    setSessions(list);
    saveSessions(list);
  };
  /** The API of `server` as its session (none when signed out there). */
  const as = (server: string) => api(server, sessions().find((s) => s.server === server)?.token);
  /** Where a team view is kept. */
  const teamKey = (server: string, id: string) => `${server} ${id}`;

  /** Forgets everything about `server`'s session. */
  function forget(server: string): void {
    keep(sessions().filter((s) => s.server !== server));
    const { [server]: _gone, ...others } = mes();
    setMes(others);
    setTeams(Object.fromEntries(Object.entries(teams()).filter(([key]) => !key.startsWith(`${server} `))));
  }

  /** Runs one call on `server`; a refusal becomes an error toast (and `null`). Signed out there: forgets the session. */
  async function attempt<T>(server: string, call: (api: AccountsApi) => Promise<T>, quiet = false): Promise<T | null> {
    try {
      return await call(as(server));
    } catch (error) {
      if (isApiError(error) && error.status === 401 && sessions().some((s) => s.server === server)) forget(server);
      if (!quiet) toast(error instanceof Error ? error.message : String(error), "error");
      return null;
    }
  }

  /** Asks `server` for the account and its teams. */
  async function refresh(server: string): Promise<void> {
    const me = await attempt(server, (a) => a.me());
    if (me) setMes({ ...mes(), [server]: me });
  }

  /** Loads a team's view. */
  async function loadTeam(server: string, id: string): Promise<void> {
    const view = await attempt(server, (a) => a.team(id));
    if (view) setTeams({ ...teams(), [teamKey(server, id)]: view });
  }

  /** After a change to team `id`: its view and the team list again. */
  async function changed(server: string, id: string): Promise<void> {
    await Promise.all([loadTeam(server, id), refresh(server)]);
  }

  /**
   * Signs in (or up) with `start`, keeping the session. A right password on an account with
   * the second step waits for its code ({@link Accounts.codeNeeded}, with `ticket` when it came
   * from Google or GitHub); `withCode` says a code was sent (a refusal then says it was wrong).
   */
  async function begin(text: string, start: (api: AccountsApi) => ReturnType<AccountsApi["signIn"]>, ticket?: string, withCode = false): Promise<boolean> {
    const server = serverAddress(text);
    if (!server) {
      toast(`'${text}' is not a server address (for example http://192.168.1.4:7878)`, "error");
      return false;
    }
    let session: Awaited<ReturnType<AccountsApi["signIn"]>>;
    try {
      // Without the old session: signing in needs none.
      session = await start(api(server));
    } catch (error) {
      if (isApiError(error) && error.secondStep) {
        setCodeNeeded(ticket ? { server, ticket } : { server });
        if (withCode) toast(error.message, "error");
        return false;
      }
      toast(error instanceof Error ? error.message : String(error), "error");
      return false;
    }
    setCodeNeeded(null);
    keep([...sessions().filter((s) => s.server !== server), { server, token: session.token, account: session.account }]);
    await refresh(server);
    return true;
  }

  const accounts: Accounts = {
    homeServer: deps.homeServer,
    sessions,
    me: (server) => mes()[server] ?? null,
    team: (server, id) => teams()[teamKey(server, id)] ?? null,
    refresh,
    loadTeam,
    signIn: (server, email, password, code) => begin(server, (a) => a.signIn(email, password, code), undefined, !!code),
    signUp: (server, email, name, password) => begin(server, (a) => a.signUp(email, name, password)),
    finishSignIn: (server, ticket, code) => begin(server, (a) => a.finishSignIn(ticket, code), ticket, !!code),
    codeNeeded,
    cancelCode: () => setCodeNeeded(null),
    async deleteAccount(server, password, code) {
      const done = await attempt(server, (a) => a.deleteAccount(password, code).then(() => true));
      if (done) forget(server);
      return !!done;
    },
    async signOut(server) {
      try {
        await as(server).signOut();
      } catch {
        // Unreachable or already signed out: forgetting the session here is what matters.
      }
      forget(server);
    },
    async createTeam(server, name) {
      const team = await attempt(server, (a) => a.createTeam(name));
      if (team) await changed(server, team.id);
      return team;
    },
    async createSpace(server, team, name) {
      const space = await attempt(server, (a) => a.createSpace(team, name));
      if (space) await loadTeam(server, team);
      return space;
    },
    async invite(server, team, role, once, limits) {
      const invite = await attempt(server, (a) => a.invite(team, role, once, limits));
      if (invite) await loadTeam(server, team);
      return invite;
    },
    async revoke(server, team, code) {
      await attempt(server, (a) => a.revoke(team, code));
      await loadTeam(server, team);
    },
    async setRole(server, team, member, role) {
      await attempt(server, (a) => a.setRole(team, member, role));
      await changed(server, team);
    },
    async removeMember(server, team, member) {
      const done = await attempt(server, (a) => a.removeMember(team, member).then(() => true));
      if (!done) return;
      // Leaving: the team is gone from the list (and its view with it).
      const me = mes()[server];
      if (me && me.account.id === member) {
        setTeams(Object.fromEntries(Object.entries(teams()).filter(([key]) => key !== teamKey(server, team))));
        await refresh(server);
      } else {
        await changed(server, team);
      }
    },
    pending,
    async offer(link) {
      setPending({ link, info: null });
      const info = await attempt(link.server, (a) => a.inviteInfo(link.code));
      if (pending()?.link.code === link.code) setPending({ link, info, gone: !info });
    },
    async accept() {
      const invite = pending();
      if (!invite) return null;
      const team = await attempt(invite.link.server, (a) => a.accept(invite.link.code));
      if (!team) return null;
      setPending(null);
      toast(`You joined ${team.name} as ${team.role}.`);
      await changed(invite.link.server, team.id);
      return team;
    },
    dismiss: () => setPending(null),
    call: (server, call, quiet) => attempt(server, call, quiet),
  };

  // Up to date at start: each server's teams, and the invite the page was opened with.
  for (const session of sessions()) void refresh(session.server);
  if (deps.invite) void accounts.offer(deps.invite);
  return accounts;
}
